/**
 * Centralized button-tier styles. Locked design hierarchy 2026-05-07
 * (Russ's call after staging review surfaced "too many same-looking navy
 * buttons" — visual fatigue across the flow).
 *
 * The hierarchy answers Russ's "lead the eye to the end" concern WITHOUT a
 * palette unlock: green-as-a-6th-color was considered and rejected at the
 * brand-discipline boundary (the 5-color palette is locked at Phase 1
 * RE-LOCK; Coral is the *single* warm accent). Visual emphasis comes from
 * size, weight, and shadow — not color.
 *
 * Tiers:
 *   - terminalAction → conversion-critical "this is the end of the flow"
 *     (Generate Blueprint / Generate Brief / Confirm consent on the email).
 *     Larger padding, larger font, drop shadow. Solid navy fill.
 *   - primary → standard navy CTAs (Sign in, Let's go, Add Profile, Send).
 *     Existing v10 grammar; no change.
 *   - ghost → outline-navy for transactional secondary actions (Cancel,
 *     Skip, Back). Already in use across the app via inline styles.
 *
 * Usage:
 *   import { terminalActionButtonStyle } from "../lib/buttonStyles.js";
 *   <button style={terminalActionButtonStyle}>Generate Blueprint</button>
 *
 * The terminal-action treatment is intentionally restrained:
 *   - Same navy fill as the primary tier (color continuity through the flow)
 *   - +4-12px padding bumps it to ~52px tall vs. the standard ~44px tall
 *   - Bumps font-size from sm (14px) to md (16px) for legibility at the
 *     terminal moment
 *   - Adds a subtle drop shadow (8px blur, navy 20% alpha) to lift the
 *     button off the surface — signals "this is the lifted/important one"
 *
 * Spec: /design/V1_Button_Hierarchy_Spec.md
 */

/**
 * Tier 1 — Terminal action (Generate Blueprint / Generate Brief).
 * Apply to the SINGLE conversion-critical button on a flow surface.
 * Don't stack two terminalAction buttons next to each other.
 */
export const terminalActionButtonStyle = {
  padding: "14px 30px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-md)",
  letterSpacing: "0.02em",
  border: "1px solid var(--ppr-color-accent-navy)",
  background: "var(--ppr-color-accent-navy)",
  color: "var(--ppr-color-text-inverse)",
  borderRadius: "var(--ppr-radius-sm)",
  cursor: "pointer",
};

/**
 * Tier 1 — disabled state (button is in flight or unavailable).
 * Same shape as terminalActionButtonStyle but with opacity dim + wait cursor.
 * Spread the base style first, then override.
 */
export const terminalActionButtonDisabledStyle = {
  ...terminalActionButtonStyle,
  cursor: "wait",
  opacity: 0.7,
  boxShadow: "0 2px 6px rgba(28, 42, 64, 0.10)",
};

/**
 * Tier 3 — Secondary (outline-navy) button. Used for OAuth providers, "Sign in
 * with Google," and other secondary CTAs that need to read as a button next
 * to a Tier-1 or Tier-2 primary on the same surface. White background, 2px
 * navy border, Inter Bold navy text — chunky-Dummies-grammar discipline so
 * it visually balances the filled navy primary buttons.
 *
 * Hover state: callers should toggle background to `var(--ppr-color-accent-pale-cyan)`
 * via React useState onMouseEnter/onMouseLeave (inline-style React doesn't
 * support :hover natively). See SignIn.jsx for the canonical usage pattern.
 *
 * Spec: /design/V1_Button_Hierarchy_Spec.md (Tier 3 section, added 2026-05-09)
 */
export const secondaryButtonStyle = {
  width: "100%",
  minHeight: 46,
  padding: "12px 16px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontWeight: 500,
  fontSize: 15,
  letterSpacing: 0,
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-accent-navy)",
  borderRadius: "var(--ppr-radius-sm)",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  transition: "background 120ms ease",
};

/**
 * Tier 3 disabled — same shape with dim + wait cursor.
 */
export const secondaryButtonDisabledStyle = {
  ...secondaryButtonStyle,
  cursor: "wait",
  opacity: 0.7,
};
